import { CalendarFill, ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { CaptionProps, DateRange, DayPicker, useNavigation } from 'react-day-picker';
import { Card, Col, Modal, Row } from 'react-bootstrap';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '@designSystem/Button';
import CalendarFormStepTwoSlot from './CalendarFormStepTwoSlot';
import FormSectionHeader from '../../../components/FormSectionHeader';
import { ICreateCalendarForm } from '../utils/interfaces';
import InformationBlock from '@designSystem/InformationBlock';
import InputWrapper from '@designSystem/InputWrapper';
import { calendarDatePickerStyle } from '../utils/calendarDatePickerStyle';
import styled from 'styled-components';
import theme from '@theme';
import { i18n } from '@i18n';
import { useTranslation } from 'react-i18next';
import { DateManager } from '../../../utils/DateManager';
import Table from '@designSystem/Table';
import { useTable, useFilters, Row as TableRow } from 'react-table';
import { useParams } from 'react-router-dom';
import { getSlotCalendar } from '@features/zonesGeoReferences/reducer/referencesAPI';
import { parse, parseISO, format } from 'date-fns';

function transformData(
  scheduleData: Array<{ day: string; max_appointments: string; schedule: { type_label: string } }>
) {
  const result: { [key: string]: { date: string; nb_morning: string; number_pm: string } } = {};
  scheduleData.forEach((entry) => {
    const day = entry.day;
    const typeLabel = entry.schedule.type_label;
    const maxAppointments = entry.max_appointments;
    if (!result[day]) {
      result[day] = { date: day, nb_morning: '0', number_pm: '0' };
    }
    if (typeLabel === 'MORNING') {
      result[day].nb_morning = maxAppointments;
    } else if (typeLabel === 'AFTERNOON') {
      result[day].number_pm = maxAppointments;
    }
  });
  return Object.values(result);
}

const StyledCardBody = styled(Card.Body)`
  padding: 24px 24px;
  color: ${theme.palette.grey[700]};
  font-size: ${theme.fonts.size.sm};
`;

const StyledCalendarInputsCustomRow = styled(Row)`
  margin-top: 1.5rem;
`;

const StyledCalendarInputsCustomRowMedium = styled(Row)`
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
`;

const StyledDayPickerHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  gap: 10px;
  width: 331px;
  height: 40px;
  justify-content: space-between;
  background: ${theme.palette.grey[50]};
  border-radius: 6px;
  color: ${theme.palette.blue[500]};
`;

const StyledCalendarFormDateInputIcon = styled.span`
  position: absolute;
  left: 13px;
  top: 6px;
`;

const daysOfWeek = [
  {
    label: () => i18n.t('features.calendars.calendarForm.monday', 'Monday'),
    value: 'slotsForMonday'
  },
  {
    label: () => i18n.t('features.calendars.calendarForm.tuesday', 'Tuesday'),
    value: 'slotsForTuesday'
  },
  {
    label: () => i18n.t('features.calendars.calendarForm.wednesday', 'Wednesday'),
    value: 'slotsForWednesday'
  },
  {
    label: () => i18n.t('features.calendars.calendarForm.thursday', 'Thursday'),
    value: 'slotsForThursday'
  },
  {
    label: () => i18n.t('features.calendars.calendarForm.friday', 'Friday'),
    value: 'slotsForFriday'
  }
];

function dateBetweenFilterFn(
  rows: TableRow<{ date: string; nb_morning: string; number_pm: string }>[],
  columnIds: string[],
  filterValue: [Date | undefined, Date | undefined]
) {
  const [start, end] = filterValue || [];
  if (!start && !end) return rows;
  const id = columnIds[0];

  const newRows = rows.filter((row) => {
    const rowDateString = row.values[id];
    if (!rowDateString) return false;
    const parsedDate = parse(rowDateString, 'yyyy-MM-dd', new Date());
    if (start && end) return parsedDate >= start && parsedDate <= end;
    if (start) {
      return parsedDate >= start;
    }
    if (end) return parsedDate <= end;
    return true;
  });
  return newRows;
}

function DateRangeColumnFilter({
  column: { filterValue = [undefined, undefined], setFilter }
}: any) {
  const [localStart, localEnd] = filterValue;
  const formatToInput = (date: Date | undefined) => (date ? format(date, 'yyyy-MM-dd') : '');
  const parseFromInput = (value: string) => (value ? parseISO(value) : undefined);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
      <input
        type="date"
        value={formatToInput(localStart)}
        onChange={(e) => {
          const val = parseFromInput(e.target.value);
          setFilter((old: [Date | undefined, Date | undefined] = [undefined, undefined]) => [
            val,
            old[1]
          ]);
        }}
      />
      <input
        type="date"
        value={formatToInput(localEnd)}
        onChange={(e) => {
          const val = parseFromInput(e.target.value);
          setFilter((old: [Date | undefined, Date | undefined] = [undefined, undefined]) => [
            old[0],
            val
          ]);
        }}
      />
    </div>
  );
}

const columnData = [
  {
    Header: 'Date',
    accessor: 'date',
    Filter: DateRangeColumnFilter,
    filter: 'dateBetween'
  },
  {
    Header: 'Créneau ouvert matin',
    accessor: 'nb_morning'
  },
  {
    Header: 'Créneau ouvert soir',
    accessor: 'number_pm'
  }
];

function CustomCaption(props: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  return (
    <StyledDayPickerHeader>
      <ChevronLeft
        style={{ cursor: 'pointer' }}
        color="blue"
        onClick={() => previousMonth && goToMonth(previousMonth)}
      />
      {new DateManager(props.displayMonth).format('MMMM YYYY')}
      <ChevronRight
        color="blue"
        style={{ cursor: 'pointer' }}
        onClick={() => nextMonth && goToMonth(nextMonth)}
      />
    </StyledDayPickerHeader>
  );
}

const CalendarFormStepTwo: React.FC<{
  formValues: ICreateCalendarForm;
  register: CallableFunction;
  setValue: CallableFunction;
  isAnUpdate?: boolean;
}> = ({ formValues, setValue, register, isAnUpdate = false }) => {
  const defaultSelected: DateRange = { from: undefined, to: undefined };
  const { calendarId } = useParams();
  const [calendarData, setCalendarData] = useState<
    { date: string; nb_morning: string; number_pm: string }[]
  >([]);
  const [range, setRange] = useState<DateRange | undefined>(defaultSelected);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (calendarId) {
      fetchCalendarData();
    }
  }, [calendarId]);

  const fetchCalendarData = async () => {
    try {
      const result = await getSlotCalendar({
        calendar_id: Number(calendarId),
        start_day: new DateManager(new Date()).format('YYYY-MM-DD'),
        end_day: new DateManager(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)).format(
          'YYYY-MM-DD'
        )
      });
      const clearData = transformData(result.data);
      setCalendarData(clearData);
    } catch (error) {
      console.error('Erreur lors de la récupération du calendrier', error);
    }
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: columnData as any,
      data: calendarData,
      filterTypes: {
        dateBetween: dateBetweenFilterFn
      },
      defaultColumn: {
        Filter: () => null
      }
    },
    useFilters
  );

  useEffect(() => {
    setValue('dateRange', getRangeStringFromState());
  }, [range]);

  const getRangeStringFromState = () => {
    const fromDateString = new DateManager(range?.from).format('DD/MM/YYYY');
    const toDateString = new DateManager(range?.to).format('DD/MM/YYYY');
    return `${fromDateString} — ${toDateString}`;
  };

  useEffect(() => {
    setValue('dateRange', getRangeStringFromState());
  }, [range]);

  const initialDateRange = useMemo(() => {
    if (!rows.length) return '';

    let minDate = rows[0].values.date;
    let maxDate = rows[0].values.date;

    rows.forEach((row) => {
      const date = row.values.date;
      if (date < minDate) minDate = date;
      if (date > maxDate) maxDate = date;
    });

    const formatDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    };

    return `${formatDate(minDate)} — ${formatDate(maxDate)}`;
  }, [rows]);

  useEffect(() => {
    if (initialDateRange) {
      setValue('dateRange', initialDateRange);
    }
  }, [initialDateRange, setValue]);

  return (
    <StyledCardBody>
      <FormSectionHeader
        headerTitle={t('features.calendars.calendarForm.indicateDate', 'Indicate dates')}
      />
      <StyledCalendarInputsCustomRow>
        <Col md={6} sm={12}>
          <InputWrapper
            inputLabel={t('features.calendars.calendarForm.chooseDate', 'Choose dates')}
          >
            <>
              <input
                style={{ paddingLeft: 35 }}
                type="text"
                id="dateRange"
                onClick={() => setIsModalOpen(true)}
                {...register('dateRange')}
                value={formValues.dateRange}
                aria-label="dateRange-input"
              />
              <StyledCalendarFormDateInputIcon>
                <CalendarFill color={theme.palette.grey[400]} />
              </StyledCalendarFormDateInputIcon>
            </>
          </InputWrapper>
        </Col>
      </StyledCalendarInputsCustomRow>
      <StyledCalendarInputsCustomRow>
        <FormSectionHeader
          headerTitle={t(
            'features.calendars.calendarForm.availableSlot',
            'Available slots and days'
          )}
          headerSubtitle={
            isAnUpdate
              ? t(
                  'features.calendars.calendarForm.makeBulk',
                  'Make a bulk update of available slots and days'
                )
              : t(
                  'features.calendars.calendarForm.maxAppointments',
                  'Indicate max appointments per day on selected days'
                )
          }
        />
      </StyledCalendarInputsCustomRow>
      <StyledCalendarInputsCustomRowMedium>
        <Col md={8} />
        <Col md={2}>{t('features.calendars.calendarForm.morning', 'Morning')}</Col>
        <Col md={2}>{t('features.calendars.calendarForm.afternoon', 'Afternoon')}</Col>
      </StyledCalendarInputsCustomRowMedium>
      {daysOfWeek.map((dayOfWeek, i) => (
        <CalendarFormStepTwoSlot
          dayOfWeek={dayOfWeek}
          key={`day-of-week-${i}`}
          register={register}
          formValues={formValues}
        />
      ))}
      {isAnUpdate && (
        <InformationBlock
          title={t(
            'features.calendars.calendarForm.displayedSlot',
            'Displayed slots values are fetched from last week of this calendar'
          )}
        />
      )}
      <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)}>
        <Modal.Body className="d-flex" style={{ maxWidth: 351, padding: '20px 10px' }}>
          <>
            <style>{calendarDatePickerStyle}</style>
            <DayPicker
              mode="range"
              components={{ Caption: CustomCaption }}
              selected={range}
              onSelect={setRange}
              defaultMonth={new Date()}
            />
          </>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between flex-row">
          <Button design="white" onClick={() => setIsModalOpen(false)}>
            {t('component.button.back', 'Back')}
          </Button>
          <Button
            onClick={() => {
              setIsModalOpen(false);
              setRange(range);
            }}
          >
            {t('component.button.save', 'Save')}
          </Button>
        </Modal.Footer>
      </Modal>
      <div style={{ margin: '60px' }} />
      <Table {...getTableProps()} maxCols={3} style={{ width: '400px' }}>
        <thead>
          {headerGroups.map((headerGroup, headerIndex) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerIndex}>
              {headerGroup.headers.map((column, columnIndex) => (
                <th
                  {...column.getHeaderProps()}
                  key={columnIndex}
                  style={{ width: '40px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {column.render('Header')}
                  {column.canFilter ? column.render('Filter') : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={headerGroups[0]?.headers.length}
                style={{ textAlign: 'center', padding: '10px' }}
              >
                Aucun rendez-vous trouvé
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={rowIndex}>
                  {row.cells.map((cell, cellIndex) => (
                    <td {...cell.getCellProps()} key={cellIndex} style={{ width: '40px' }}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </StyledCardBody>
  );
};

export default CalendarFormStepTwo;
